import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Table, Form, Button } from 'react-bootstrap';
import { FaEdit, FaCheck, FaTimes, FaTrash, FaFilter, FaBook, FaFolder, FaFileAlt } from 'react-icons/fa';

const CuentasTableView = ({ data = [], catalogoSAT = [], onEditAccount, onDeleteAccount }) => {
  const [editingId, setEditingId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);

  const handleEditClick = (cuenta) => {
    setEditingId(cuenta.idCuentaContable);
    setEditFormData({
      idCuentaPadre: cuenta.idCuentaPadre || '',
      vCodigoCuenta: cuenta.vCodigoCuenta || '',
      vNombreCuenta: cuenta.vNombreCuenta || '',
      idCatCuentaContableSAT: cuenta.idCatCuentaContableSAT || '',
      iTipoNivelCuenta: cuenta.iTipoNivelCuenta || ''
    });
  };

  const handleCancelClick = () => {
    setEditingId(null);
    setEditFormData({});
  };

  const handleSaveClick = (cuentaOriginal) => {
    const updatedAccount = { ...cuentaOriginal, ...editFormData };
    if (onEditAccount) {
      onEditAccount(updatedAccount, true);
    }
    setEditingId(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(data.map(c => c.idCuentaContable));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectRow = (e, id) => {
    if (e.target.checked) {
      setSelectedIds(prev => [...prev, id]);
    } else {
      setSelectedIds(prev => prev.filter(selectedId => selectedId !== id));
    }
  };

  const getParentText = (idCuentaPadre) => {
    if (!idCuentaPadre) return <span className="text-muted fst-italic">Ninguna</span>;
    const parent = data.find(c => c.idCuentaContable?.toString() === idCuentaPadre?.toString());
    return parent ? `${parent.vCodigoCuenta} - ${parent.vNombreCuenta}` : <span className="text-muted">Desconocida</span>;
  };

  const getSatText = (idSat) => {
    if (!idSat) return <span className="text-danger fst-italic">Sin asignar</span>;
    const sat = catalogoSAT.find(s => s.idCatCuentaContable?.toString() === idSat?.toString());
    return sat ? `${sat.vCodigoCuenta}` : <span className="text-muted">Desconocida</span>;
  };

  const getTipoText = (idTipo) => {
    if (!idTipo) return '';
    const idStr = idTipo?.toString();
    if (idStr === '3') return <span className="d-flex align-items-center"><FaFileAlt className="text-secondary me-1" /> D</span>;
    if (idStr === '2') return <span className="d-flex align-items-center"><FaFolder className="text-warning me-1" /> M</span>;
    if (idStr === '1') return <span className="d-flex align-items-center"><FaBook className="text-info me-1" /> T</span>;
    return '';
  };

  const allSelected = data.length > 0 && selectedIds.length === data.length;

  return (
    <div className="table-responsive bg-white rounded border" style={{ maxHeight: '600px', overflowY: 'auto' }}>
      <style>{`
        .ultra-compact-table td, .ultra-compact-table th {
          padding: 0.15rem 0.3rem !important;
          vertical-align: middle !important;
        }
        .ultra-compact-table {
          font-size: 0.75rem !important;
          line-height: 1.1 !important;
        }
      `}</style>

      <div className="bg-light p-1 border-bottom d-flex justify-content-between align-items-center" style={{ fontSize: '0.8rem' }}>
        <div>
          <span className="me-3 fw-bold text-primary">{selectedIds.length} seleccionada(s)</span>
          <Button
            variant="outline-primary"
            size="sm"
            className="me-2"
            style={{ padding: '0.1rem 0.4rem', fontSize: '0.75rem' }}
            disabled={selectedIds.length !== 1}
            onClick={() => {
              const selectedCuenta = data.find(c => c.idCuentaContable === selectedIds[0]);
              if (selectedCuenta) handleEditClick(selectedCuenta);
            }}
          >
            <FaEdit className="me-1" /> Editar
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            className="me-2"
            style={{ padding: '0.1rem 0.4rem', fontSize: '0.75rem' }}
            disabled={selectedIds.length === 0}
            onClick={() => {
              const selectedCuentas = data.filter(c => selectedIds.includes(c.idCuentaContable));
              if (selectedCuentas.length > 0) onDeleteAccount(selectedCuentas);
            }}
          >
            <FaTrash className="me-1" /> Eliminar
          </Button>
        </div>
        <div>
          <Button
            variant="outline-secondary"
            size="sm"
            style={{ padding: '0.1rem 0.4rem', fontSize: '0.75rem' }}
            onClick={() => alert("Filtros (Pendiente de implementar)")}
          >
            <FaFilter className="me-1" /> Filtrar
          </Button>
        </div>
      </div>
      <Table hover striped bordered className="mb-0 text-nowrap ultra-compact-table">
        <thead className="bg-light sticky-top">
          <tr>
            <th className="text-center" style={{ width: '40px' }}>
              <Form.Check
                type="checkbox"
                checked={allSelected}
                onChange={handleSelectAll}
                disabled={data.length === 0}
              />
            </th>
            <th>Código</th>
            <th>Nombre</th>
            <th>Tipo</th>
            <th>Cuenta Padre</th>
            <th>SAT</th>
            <th>Naturaleza</th>
            <th>Nivel</th>
            <th className="text-center">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan="9" className="text-center py-4 text-muted">
                No hay cuentas para mostrar.
              </td>
            </tr>
          ) : (
            data.map((cuenta) => (
              <tr key={cuenta.idCuentaContable} className={selectedIds.includes(cuenta.idCuentaContable) ? 'table-primary' : ''}>
                <td className="text-center">
                  <Form.Check
                    type="checkbox"
                    checked={selectedIds.includes(cuenta.idCuentaContable)}
                    onChange={(e) => handleSelectRow(e, cuenta.idCuentaContable)}
                  />
                </td>
                <td>
                  {editingId === cuenta.idCuentaContable ? (
                    <Form.Control
                      size="sm"
                      name="vCodigoCuenta"
                      value={editFormData.vCodigoCuenta}
                      onChange={handleChange}
                      style={{ minWidth: '100px' }}
                    />
                  ) : (
                    <span className="fw-bold text-primary">{cuenta.vCodigoCuenta}</span>
                  )}
                </td>
                <td>
                  {editingId === cuenta.idCuentaContable ? (
                    <Form.Control
                      size="sm"
                      name="vNombreCuenta"
                      value={editFormData.vNombreCuenta}
                      onChange={handleChange}
                      style={{ minWidth: '150px' }}
                    />
                  ) : (
                    cuenta.vNombreCuenta
                  )}
                </td>
                <td>
                  {editingId === cuenta.idCuentaContable ? (
                    <Form.Select
                      size="sm"
                      name="iTipoNivelCuenta"
                      value={editFormData.iTipoNivelCuenta}
                      onChange={handleChange}
                      style={{ minWidth: '130px' }}
                    >
                      <option value="1">Título</option>
                      <option value="2">Mayor / Agrupadora</option>
                      <option value="3">De Detalle</option>
                    </Form.Select>
                  ) : (
                    getTipoText(cuenta.iTipoNivelCuenta)
                  )}
                </td>
                <td className="text-truncate" style={{ maxWidth: '150px' }} title={getParentText(cuenta.idCuentaPadre)}>
                  {editingId === cuenta.idCuentaContable ? (
                    <Form.Select
                      size="sm"
                      name="idCuentaPadre"
                      value={editFormData.idCuentaPadre}
                      onChange={handleChange}
                      style={{ minWidth: '150px' }}
                    >
                      <option value="">Ninguna</option>
                      {data.map(c => (
                        <option key={c.idCuentaContable} value={c.idCuentaContable}>
                          {c.vCodigoCuenta} - {c.vNombreCuenta}
                        </option>
                      ))}
                    </Form.Select>
                  ) : (
                    getParentText(cuenta.idCuentaPadre)
                  )}
                </td>
                <td>
                  {editingId === cuenta.idCuentaContable ? (
                    <Form.Select
                      size="sm"
                      name="idCatCuentaContableSAT"
                      value={editFormData.idCatCuentaContableSAT}
                      onChange={handleChange}
                      style={{ minWidth: '120px' }}
                    >
                      <option value="">Sin asignar</option>
                      {catalogoSAT.map(c => (
                        <option key={c.idCatCuentaContable} value={c.idCatCuentaContable}>
                          {c.vCodigoCuenta} - {c.vNombreCuenta}
                        </option>
                      ))}
                    </Form.Select>
                  ) : (
                    getSatText(cuenta.idCatCuentaContableSAT)
                  )}
                </td>
                <td>
                  {cuenta.vNaturaleza === 'D' ? 'Deudora' : 'Acreedora'}
                </td>
                <td className="text-center">{cuenta.iNivel}</td>
                <td className="text-center">
                  {editingId === cuenta.idCuentaContable ? (
                    <>
                      <Button variant="link" className="text-success p-0 me-2" title="Guardar" onClick={() => handleSaveClick(cuenta)}>
                        <FaCheck />
                      </Button>
                      <Button variant="link" className="text-secondary p-0" title="Cancelar" onClick={handleCancelClick}>
                        <FaTimes />
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button variant="link" className="text-primary p-0 me-3" title="Editar" onClick={() => handleEditClick(cuenta)}>
                        <FaEdit />
                      </Button>
                      <Button variant="link" className="text-danger p-0" title="Eliminar" onClick={() => onDeleteAccount && onDeleteAccount(cuenta)}>
                        <FaTrash />
                      </Button>
                    </>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </div>
  );
};

CuentasTableView.propTypes = {
  data: PropTypes.array.isRequired,
  catalogoSAT: PropTypes.array,
  onEditAccount: PropTypes.func,
  onDeleteAccount: PropTypes.func
};

export default CuentasTableView;
